<template>
  <el-card>
    <!-- 搜索表单 -->
    <div class="top">
      <div class="searchForm">
        <el-form
          :inline="true"
          ref="searchForm"
          :model="searchForm"
          class="searchForm"
          @submit.native.prevent
          @keyup.enter.native="searchList"
        >
          <el-form-item prop="query" class="query-input">
            <div class="codemirror-container">
              <codemirror
                ref="codemirrorEditor"
                v-model="searchForm.query"
                :options="codemirrorOptions"
                @input="onQueryChange"
              />
            </div>
          </el-form-item>
          <el-form-item prop="timeRange" class="time-picker">
            <el-date-picker
              size="small"
              v-model="searchForm.timeRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              format="yyyy-MM-dd HH:mm:ss"
              value-format="yyyy-MM-dd HH:mm:ss"
              :picker-options="pickerOptions"
              @change="onTimeRangeChange"
            />
          </el-form-item>
          <el-form-item class="action-buttons">
            <el-button type="primary" @click="searchList" :loading="loading">查 询</el-button>
            <el-button @click="resetForm">重 置</el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>

    <!-- 总条数显示 -->
    <div v-if="list.length > 0" class="total-count">
      <span v-if="totalCount">
        日志条数
        <strong>{{ totalCount }}</strong>
        条，
      </span>
      <span>
        已加载
        <strong>{{ list.length }}</strong>
        条日志
      </span>
      <span v-if="isFinished" class="finished-tip">（已加载完毕）</span>
    </div>

    <!-- 表格 -->
    <el-table
      :data="list"
      class="cls-table"
      :default-sort="{ prop: 'report_time', order: 'descending' }"
      @sort-change="handleSortChange"
    >
      <el-table-column
        label="日志时间"
        prop="report_time"
        width="230"
        sortable="custom"
        :sort-orders="['descending', 'ascending']"
      >
        <template slot-scope="scope">
          <span class="report-time-text">{{ formatTime(scope.row.report_time) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="日志数据" prop="log" min-width="400">
        <template slot-scope="scope">
          <div class="msg-content">
            <div class="msg-text">
              <el-tag>msg:</el-tag>
              <template v-if="scope.row.needCollapse">
                <span v-if="scope.row.expanded" v-html="formatMsg(scope.row.log)"></span>
                <span v-else v-html="formatMsg(scope.row.log.substring(0, 1000) + '...')"></span>
                <a
                  type="text"
                  size="small"
                  @click="toggleMsgExpand(scope.row, scope.$index)"
                  class="expand-btn-inline"
                >
                  <template v-if="scope.row.expanded">收起</template>
                  <template v-else>更多</template>
                </a>
              </template>
              <span v-else v-html="formatMsg(scope.row.log)"></span>
            </div>
            <div>
              <el-tag>uin:</el-tag>
              <span>{{ scope.row.uin }}</span>
            </div>
            <div>
              <el-tag>trace_id:</el-tag>
              <span>{{ scope.row.trace_id }}</span>
            </div>
          </div>
        </template>
      </el-table-column>
      <!-- <el-table-column label="UIN" prop="uin" width="120" />
      <el-table-column label="TraceID" prop="trace_id" width="200">
        <template slot-scope="scope">
          <span class="traceid-text">{{ scope.row.trace_id }}</span>
        </template>
      </el-table-column> -->
    </el-table>

    <!-- 加载更多 -->
    <div v-if="!isFinished && !loading && context" class="load-more">
      <el-button @click="loadMore" :loading="loadingMore">加载更多</el-button>
    </div>

    <!-- 无数据提示 -->
    <div v-if="!loading && list.length === 0" class="no-data">
      <el-empty description="暂无数据" />
    </div>

    <!-- 返回到顶部按钮 -->
    <transition name="fade">
      <div v-show="showBackToTop" class="back-to-top" @click="scrollToTop">
        <i class="el-icon-top"></i>
      </div>
    </transition>
  </el-card>
</template>

<script>
import { codemirror } from 'vue-codemirror';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/idea.css';
import dayjs from 'dayjs';
import 'codemirror/addon/display/placeholder.js';
import 'codemirror/addon/edit/closebrackets.js';
import 'codemirror/addon/edit/matchbrackets.js';
import 'codemirror/addon/selection/active-line.js';

export default {
  name: 'ClsLogs',
  components: {
    codemirror,
  },
  data() {
    return {
      // 搜索表单
      searchForm: {
        query: '',
        timeRange: null,
        sort: 'desc', // 排序方式：desc降序，asc升序
      },
      // 分页参数
      pageSize: 100, // 默认每页100条
      context: null, // 用于获取更多数据的标志
      isFinished: false, // 是否已经加载完毕
      totalCount: null, // 总日志条数
      // 表格数据
      loading: false,
      loadingMore: false,
      list: [],
      // 返回到顶部按钮
      showBackToTop: false,
      // CodeMirror配置
      codemirrorOptions: {
        mode: 'lucene',
        theme: 'idea',
        lineNumbers: true,
        lineWrapping: true,
        placeholder: '请输入Lucene查询语句，例如：trace_id:XXXXXX AND msg:XXXXXX',
        autoCloseBrackets: true,
        matchBrackets: true,
        styleActiveLine: true,
        height: 'auto',
        viewportMargin: Infinity,
        readOnly: false,
        cursorBlinkRate: 530,
        // 启用行号显示
        foldGutter: false,
        gutters: ['CodeMirror-linenumbers'],
        // 自定义快捷键：Ctrl+Enter 或 Cmd+Enter 换行
        extraKeys: {
          'Ctrl-Enter'(cm) {
            // Ctrl+Enter 换行
            cm.replaceSelection('\n');
          },
          'Cmd-Enter'(cm) {
            // Cmd+Enter 换行 (Mac)
            cm.replaceSelection('\n');
          },
          Enter() {
            // 普通回车键不换行
            return false;
          },
        },
      },
      // CodeMirror高度配置常量
      codeMirrorConfig: {
        lineHeight: 14,
        minHeight: 30,
        maxHeight: 200,
        padding: 16,
      },
      // 时间选择器快捷选项
      pickerOptions: {
        shortcuts: [
          {
            text: '最近5分钟',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 5 * 60 * 1000);
              picker.$emit('pick', [start, end]);
            },
          },
          {
            text: '最近15分钟',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 15 * 60 * 1000);
              picker.$emit('pick', [start, end]);
            },
          },
          {
            text: '最近30分钟',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 30 * 60 * 1000);
              picker.$emit('pick', [start, end]);
            },
          },
          {
            text: '最近1小时',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000);
              picker.$emit('pick', [start, end]);
            },
          },
          {
            text: '最近3小时',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 3);
              picker.$emit('pick', [start, end]);
            },
          },
          {
            text: '最近6小时',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 6);
              picker.$emit('pick', [start, end]);
            },
          },
          {
            text: '最近12小时',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 12);
              picker.$emit('pick', [start, end]);
            },
          },
          {
            text: '最近1天',
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24);
              picker.$emit('pick', [start, end]);
            },
          },
        ],
      },
    };
  },
  async created() {
    this.setDefaultTimeRange();
    await this.initCodeMirror();
  },
  async mounted() {
    // 页面加载完成后自动执行一次搜索
    await this.searchList();
    // 添加滚动监听
    this.addScrollListener();
  },
  beforeDestroy() {
    // 移除滚动监听
    this.removeScrollListener();
  },
  methods: {
    // 初始化CodeMirror
    async initCodeMirror() {
      // 定义Lucene模式
      const CodeMirrorOri = require('codemirror/lib/codemirror');

      // 检查是否已经定义了lucene模式
      if (!CodeMirrorOri.modes.lucene) {
        console.log('注册Lucene模式');
        // 注册Lucene模式
        CodeMirrorOri.defineMode('lucene', function () {
          return {
            token(stream) {
              // 布尔操作符 - 使用更精确的匹配，避免在单词中间高亮
              const { pos } = stream;
              const match = stream.match(/\b(AND|OR|NOT)\b/);
              if (match) {
                // 检查前面和后面的字符，确保不是字母数字下划线
                const before = pos > 0 ? stream.string.charAt(pos - 1) : ' ';
                const after =
                  pos + match[0].length < stream.string.length
                    ? stream.string.charAt(pos + match[0].length)
                    : ' ';

                // 如果前后都是非字母数字下划线字符，则高亮
                if (!/[a-zA-Z0-9_]/.test(before) && !/[a-zA-Z0-9_]/.test(after)) {
                  return 'keyword';
                }
              }
              // 字段名
              if (stream.match(/[a-zA-Z_][a-zA-Z0-9_]*\s*:/)) {
                return 'type';
              }
              // 引号字符串
              if (stream.match(/"[^"]*"/)) {
                return 'string';
              }
              // 单引号字符串
              if (stream.match(/'[^']*'/)) {
                return 'string';
              }
              // 通配符
              if (stream.match(/[*?]/)) {
                return 'keyword';
              }
              // 范围查询符号
              if (stream.match(/[[\]{}]/)) {
                return 'bracket';
              }
              // 括号
              if (stream.match(/[()]/)) {
                return 'bracket';
              }
              // 操作符
              if (stream.match(/[+\-~^]/)) {
                return 'operator';
              }
              // TO关键字
              if (stream.match(/\bTO\b/i)) {
                return 'keyword';
              }
              // 数字
              if (stream.match(/\d+(\.\d+)?/)) {
                return 'number';
              }
              // 跳过空白
              if (stream.eatSpace()) {
                return null;
              }
              // 其他文本
              stream.next();
              return 'text';
            },
          };
        });
        console.log('Lucene模式注册完成');
      } else {
        console.log('Lucene模式已存在');
      }

      // 等待CodeMirror实例创建后，设置运行时监听器
      this.$nextTick(() => {
        if (this.$refs.codemirrorEditor && this.$refs.codemirrorEditor.codemirror) {
          const cm = this.$refs.codemirrorEditor.codemirror;
          const self = this;

          // 监听内容变化，自动调整高度
          cm.on('change', () => {
            setTimeout(() => {
              self.adjustCodeMirrorHeight(cm);
            }, 0);
          });
        }
      });
    },

    // 工具方法：生成随机数
    randomInt(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    },

    // 自动调整CodeMirror高度
    adjustCodeMirrorHeight(cm) {
      if (!cm) return;

      const lineCount = cm.lineCount();
      const { lineHeight, minHeight, maxHeight, padding } = this.codeMirrorConfig;

      // 计算新高度
      let newHeight = Math.max(lineCount * lineHeight + padding, minHeight);
      newHeight = Math.min(newHeight, maxHeight);

      // 设置新高度
      cm.setSize(null, `${newHeight}px`);
    },

    // 查询内容变化处理
    onQueryChange(value) {
      this.searchForm.query = value;

      // 自动调整CodeMirror高度
      this.$nextTick(() => {
        if (this.$refs.codemirrorEditor && this.$refs.codemirrorEditor.codemirror) {
          this.adjustCodeMirrorHeight(this.$refs.codemirrorEditor.codemirror);
        }
      });
    },

    // 设置默认时间范围
    setDefaultTimeRange() {
      const now = new Date();
      const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60 * 1000);
      this.searchForm.timeRange = [fifteenMinutesAgo, now];
    },

    // 时间范围变化处理
    onTimeRangeChange(value) {
      // value 是一个数组 [startTime, endTime] 或者 null
      console.log('时间范围变化:', value);
    },

    // 查询日志
    async searchList() {
      this.loading = true;
      this.context = null; // 重置context
      this.isFinished = false; // 重置完成状态
      this.list = [];

      try {
        await this.getList();
      } catch (error) {
        console.error('查询失败:', error);
        this.$message.error('查询失败，请重试');
      } finally {
        this.loading = false;
      }
    },

    // 获取日志列表
    async getList() {
      const params = {
        query: this.searchForm.query,
        from: this.searchForm.timeRange ? new Date(this.searchForm.timeRange[0]).getTime() : null,
        to: this.searchForm.timeRange ? new Date(this.searchForm.timeRange[1]).getTime() : null,
        limit: this.pageSize,
        sort: this.searchForm.sort, // 添加排序参数
      };

      // 只有在加载更多时才传递context
      if (this.context) {
        params.context = this.context;
      }

      try {
        const result = await this.$request({
          url: this.$api.searchLogV2,
          data: params,
        });

        const { context, is_finished, results, total_count } = result;

        // 更新状态
        this.context = context;
        this.isFinished = is_finished;
        this.totalCount = total_count;

        // 处理数据
        if (this.list.length === 0) {
          // 首次加载，替换数据
          this.list = results || [];
        } else {
          // 加载更多，追加数据
          this.list = this.list.concat(results || []);
        }

        // 计算消息行数和是否需要折叠
        this.list.forEach((item) => {
          if (item.log) {
            item.logLines = item.log.split('\n').length;
            // 基于字符数量判断是否需要折叠
            this.$set(item, 'needCollapse', item.log.length > 1000);
            this.$set(item, 'expanded', false);
          }
        });
      } catch (error) {
        console.error('获取日志列表失败:', error);
        throw error;
      }
    },

    // 格式化消息内容
    formatMsg(msg) {
      if (!msg) return '';

      // 简单的HTML转义和换行处理
      return msg
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
    },

    // 切换消息展开状态
    toggleMsgExpand(row, index) {
      this.$set(this.list[index], 'expanded', !row.expanded);
    },

    // 加载更多
    async loadMore() {
      if (this.loadingMore || this.isFinished || !this.context) return;

      this.loadingMore = true;

      // 保存当前滚动位置
      const appMainElement = document.querySelector('.app-main');
      const currentScrollTop = appMainElement ? appMainElement.scrollTop : 0;

      try {
        await this.getList();

        // 加载完成后恢复滚动位置
        this.$nextTick(() => {
          if (appMainElement) {
            appMainElement.scrollTop = currentScrollTop;
          }
        });
      } catch (error) {
        console.error('加载更多失败:', error);
        this.$message.error('加载更多失败');
      } finally {
        this.loadingMore = false;
      }
    },

    // 格式化时间
    formatTime(timestamp) {
      if (!timestamp) return '';
      return dayjs(timestamp).format('YYYY:MM:DD HH:mm:ss.SSS');
    },

    // 处理排序变化
    async handleSortChange({ prop, order }) {
      console.log(1111111111, prop, order);
      if (prop === 'report_time') {
        // 根据排序方向设置sort参数
        if (order === 'descending') {
          this.searchForm.sort = 'desc';
        } else if (order === 'ascending') {
          this.searchForm.sort = 'asc';
        } else {
          // 取消排序，恢复默认
          this.searchForm.sort = 'desc';
        }

        // 重置分页状态并从第一页开始查询
        this.context = null;
        this.isFinished = false;
        this.list = [];

        // 重新执行查询
        await this.searchList();
      }
    },

    // 添加滚动监听
    addScrollListener() {
      const appMainElement = document.querySelector('.app-main');
      if (appMainElement) {
        appMainElement.addEventListener('scroll', this.handleScroll);
      }
    },

    // 移除滚动监听
    removeScrollListener() {
      const appMainElement = document.querySelector('.app-main');
      if (appMainElement) {
        appMainElement.removeEventListener('scroll', this.handleScroll);
      }
    },

    // 处理滚动事件
    handleScroll() {
      const appMainElement = document.querySelector('.app-main');
      if (appMainElement) {
        // 当滚动超过300px时显示按钮
        this.showBackToTop = appMainElement.scrollTop > 300;
      }
    },

    // 返回到顶部
    scrollToTop() {
      const appMainElement = document.querySelector('.app-main');
      if (appMainElement) {
        appMainElement.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    },

    // 重置表单
    resetForm() {
      this.searchForm.query = '';
      this.searchForm.sort = 'desc'; // 重置排序为默认值
      this.setDefaultTimeRange();

      this.list = [];
      this.context = null;
      this.isFinished = false;
      this.totalCount = null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/element/input.scss';
@import '@/styles/element/select.scss';
@import '@/styles/element/button.scss';
@import '@/styles/element/tab.scss';
@import '@/styles/element/table.scss';
@import '@/styles/element/dropdown.scss';
@import '@/styles/element/pagination.scss';

.top {
  display: flex;
  margin-bottom: 0;
  .searchForm {
    flex: 1;

    .el-form {
      display: flex;
      align-items: flex-start;
      gap: 8px;

      .el-form-item {
        margin-bottom: 16px;

        &.query-input {
          flex: 1;
          min-width: 400px;
          margin-right: 0;
          ::v-deep {
            .el-form-item__content {
              width: 100%;
            }
          }
        }

        &.time-picker {
          flex-shrink: 0;
          margin-right: 0;
        }

        &.action-buttons {
          flex-shrink: 0;
        }
      }
    }
  }
}

// CodeMirror 样式
.codemirror-container {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  background-color: #fff;
  margin: 4px 0;
  ::v-deep .CodeMirror {
    min-height: 30px;
    max-height: 200px;
    min-width: 400px;
    width: 100%;
    font-size: 13px;
    line-height: 1.4;
    background-color: #fff !important;
    height: auto;
    pre.CodeMirror-line-like {
      color: #aaa;
    }
    .cm-keyword {
      color: #409eff;
    }
  }
}

// 总条数显示
.total-count {
  margin-bottom: 16px;
  padding: 12px 16px;
  background-color: #f5f7fa;
  border-radius: 4px;
  font-size: 14px;
  color: #606266;

  strong {
    color: #409eff;
    font-weight: 600;
  }

  .finished-tip {
    color: #67c23a;
    margin-left: 8px;
  }
}

// 表格样式
.cls-table {
  .traceid-text {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 12px;
    color: #606266;
    word-break: break-all;
  }

  .report-time-text {
    font-size: 12px;
    color: #606266;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  }

  .msg-content {
    .msg-text {
      font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      font-size: 12px;
      line-height: 1.4;
      color: #303133;
      white-space: pre-wrap;
      word-break: break-word;
    }

    .expand-btn {
      margin-top: 8px;
      padding: 0;
      font-size: 12px;
      color: #409eff;

      &:hover {
        color: #66b1ff;
      }
    }

    .expand-btn-inline {
      display: inline;
      margin-left: 12px;
      padding: 0;
      font-size: 12px;
      color: #409eff;
      vertical-align: baseline;

      &:hover {
        color: #66b1ff;
      }
    }
  }
}

// 加载更多按钮
.load-more {
  text-align: center;
  margin-top: 20px;
  padding: 20px;
}

// 无数据提示
.no-data {
  text-align: center;
  padding: 40px 0;
}

// 响应式设计
@media (max-width: 1200px) {
  .top {
    .searchForm {
      .el-form {
        flex-wrap: wrap;

        .el-form-item {
          &.query-input {
            min-width: 100%;
            margin-bottom: 16px;
          }

          &.time-picker {
            width: 300px;
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .top {
    .searchForm {
      .el-form {
        flex-direction: column;

        .el-form-item {
          width: 100% !important;

          &.time-picker {
            width: 100% !important;
          }
        }
      }
    }
  }

  .codemirror-container {
    ::v-deep .CodeMirror {
      min-width: 100%;
    }
  }

  .el-date-editor {
    width: 100% !important;
  }
}

// 表单样式优化
.el-form-item {
  margin-bottom: 16px;

  .el-form-item__label {
    font-weight: 500;
    color: #606266;
  }

  // 隐藏label
  &.query-input,
  &.time-picker,
  &.action-buttons {
    .el-form-item__label {
      display: none;
    }
  }
}

// 时间选择器样式
.el-date-editor {
  width: 350px;
}

// 选择器样式
.el-select {
  width: 150px;
}

// 返回到顶部按钮样式
.back-to-top {
  position: fixed;
  right: 30px;
  bottom: 30px;
  width: 50px;
  height: 50px;
  background-color: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  z-index: 1000;

  &:hover {
    background-color: #f5f7fa;
    border-color: #409eff;
    transform: translateY(-2px);
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.15);
  }

  i {
    color: #606266;
    font-size: 20px;
  }

  &:hover i {
    color: #409eff;
  }
}

// 淡入淡出动画
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
